
import _ from "lodash";
import Vue, { PropType } from "vue";
import { DataOptions, DataTableHeader } from "vuetify";
import { DateRangeFilterUtil } from "@/@shell/components/BaseDataTableFilter/DateRangeFilterUtil";
import {
  DcmDocumentListConfig,
  DcmDocumentListQuery,
  PipedComponent,
} from "@/@modules/dcm/interfaces/DcmDocumentListConfiguration";
import { DcmMetadataForm } from "../interfaces/DcmMetadataConfig";
import { DcmService } from "@/@modules/dcm/services/";
import CreateDocument from "@/@modules/dcm/modals/CreateDocument.vue";
import DeleteWarning from "@/@modules/dcm/modals/DeleteWarning.vue";
import PreviewDocument from "@/@modules/dcm/modals/PreviewDocument.vue";
import UploadDocument from "../modals/UploadDocument.vue";
import ViewOrEditDcmProperties from "../modals/ViewOrEditDcmProperties.vue";

export default Vue.extend({
  name: "DocumentList",
  props: {
    config: {
      type: Object as PropType<DcmDocumentListConfig<DcmDocumentListQuery>>,
      required: true,
    },
    // optionnal props
    disableSort: {
      type: Boolean,
      default: false,
      required: false,
    },
    getConfigForExpandedItem: {
      type: Function as PropType<(item: any) => DcmDocumentListConfig<DcmDocumentListQuery>>,
      required: false,
    },
    hideDefaultFooter: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLoadedOnMount: {
      type: Boolean,
      required: false,
      default: true,
    },
    isSubVDataTable: {
      type: Boolean,
      required: false,
      default: false,
    },
    isToolbarShown: {
      type: Boolean,
      required: false,
      default: true,
    },
    showExpand: {
      type: Boolean,
      required: false,
      default: false,
    },
    subVdtId: {
      type: String,
      required: false,
      default: "",
    },
  },
  data() {
    return {
      configOfExpandedItems: [] as DcmDocumentListConfig<DcmDocumentListQuery>[],
      expanded: [],
      expandIcon: "far fa-plus-square",
      expandIconEls: {} as NodeListOf<Element>,
      hasLaunchFirstRequest: false,
      hasMoreElements: false,
      items: [] as any[],
      itemSelected: [],
      loading: true,
      options: {} as DataOptions,
      previousQuery: {},
      totalElements: 0,
      totalQuery: {
        isInError: false,
        count: null as number | null,
      },
    };
  },
  async mounted() {
    if (this.isLoadedOnMount) {
      await this.refresh();
    } else {
      this.loading = false;
    }
  },
  watch: {
    "config.query.where": {
      handler(newValue, oldValue) {
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          this.options.page = 1;
          this.refresh();
        }
      },
      deep: true,
    },
    options: {
      handler(newValue, oldValue) {
        if (!_.isEmpty(oldValue)) {
          this.refresh({ isLaunchedByOptions: true });
        }
      },
      deep: true,
    },
  },
  computed: {
    headers(): (DataTableHeader & { pipe?: (item: any) => string } & {
      pipeComponent?: (item: any) => PipedComponent | null;
    })[] {
      const headers: DataTableHeader[] = this.config.columns.map(column => {
        return {
          text: this.$tc(column.label),
          value: column.key,
          align: column.align,
          class: column.class,
          sortable: column.sortable,
          pipe: column.pipe,
          pipeComponent: column.pipeComponent,
        };
      });
      return this.config.actions && !headers.find(h => h.value === "actions")
        ? [...headers, { text: this.$tc("dcm.actions"), value: "actions", sortable: false }]
        : headers;
    },
    canDeleteMultipleRows(): boolean {
      return !!this.config.actions?.delete?.multipleRows;
    },
  },
  methods: {
    expand(item: any, slot: any) {
      if (this.showExpand) {
        return slot.expand(!slot.isExpanded);
      }
    },
    getItemName(item: any) {
      return typeof this.config.itemName == "function" ? this.config.itemName(item) : item[this.config.itemName];
    },
    async getQuery() {
      // deep copy query
      let query = JSON.parse(JSON.stringify(this.config.query)) as DcmDocumentListQuery;
      if (this.config.updateQueryBeforeRefresh) {
        query = await this.config.updateQueryBeforeRefresh(query);
      }
      // default orderBy to keep as last orderBy arg if not replaced by column sortBy
      const orderByToKeep =
        Array.isArray(query.orderBy) && Array.isArray(this.options.sortBy)
          ? query.orderBy.filter(entry => !this.options.sortBy.includes(entry.value))
          : [];

      // concatenated column sortBy with orderBy to keep
      const columnSortBy =
        this.options.sortBy && this.options.sortBy.length > 0
          ? this.options.sortBy
              .map((sortBy, index) => {
                return { value: sortBy, desc: this.options.sortDesc[index] };
              })
              .concat(orderByToKeep)
          : null;

      query.orderBy = columnSortBy ?? query.orderBy ?? [];
      query.limit = this.options.itemsPerPage ?? this.config.limit.default;
      query.offset = (this.options.page - 1 || 0) * query.limit;

      // modify dateRangeTo (j+1) to include date in search
      query.where = DateRangeFilterUtil.modifyDateRangeTo(query.where);

      return query;
    },
    getTotalQuery(query: DcmDocumentListQuery) {
      // deep copy query
      const queryCopy = JSON.parse(JSON.stringify(query)) as DcmDocumentListQuery;

      queryCopy.select = [{ value: this.config.itemKey }];
      // set query to find "ALL" ids (arbitrary limit)
      // but total max. received in query by Alfresco === 1000 (?)
      queryCopy.limit = 1000000;
      queryCopy.offset = 0;

      return queryCopy;
    },
    onClickRow(item: any, slot: any, event: PointerEvent) {
      this.config.onClickRow ? this.config.onClickRow(item, slot, event) : this.expand(item, slot);
    },
    toggleExpandIcon() {
      if (this.showExpand) {
        this.expandIconEls = document.querySelectorAll(".v-data-table__expand-icon");
        this.$nextTick(() => {
          for (const icon of this.expandIconEls) {
            const isExpandedRow = icon.classList.contains("v-data-table__expand-icon--active");
            icon.classList.remove(`${isExpandedRow ? "fa-plus-square" : "fa-minus-square"}`);
            icon.classList.add(`${isExpandedRow ? "fa-minus-square" : "fa-plus-square"}`);
          }
        });
      }
    },
    async createDocument() {
      await this.$modal.show(CreateDocument, JSON.parse(JSON.stringify(this.config.actions?.create?.options)));
      this.refresh({ isForced: true });
    },
    async customRowActionFn(index: number, item: any, event: PointerEvent) {
      const customRowActions = this.config.actions?.customRowActions;
      if (customRowActions) {
        const response = await customRowActions[index].action(item, event);
        if (response && customRowActions[index].hasToForceRefresh) {
          this.refresh({ isForced: true });
        }
      }
    },
    async deleteDocument(objectId: string) {
      await DcmService.delete(objectId);
    },
    async deleteMultipleDocuments() {
      const hasConfirmed: boolean = await this.$modal.show<boolean>(DeleteWarning, {
        docsToDelete: this.itemSelected.map(item => {
          return this.getItemName(item);
        }),
      });
      if (hasConfirmed) {
        for (const item of this.itemSelected) {
          await this.deleteDocument(item[this.config.itemKey]);
        }
        this.itemSelected = [];
        this.refresh({ isForced: true });
      }
    },
    async deleteSingleDocument(item: any) {
      const hasConfirmed: boolean = await this.$modal.show<boolean>(DeleteWarning, {
        docsToDelete: [this.getItemName(item)],
      });
      if (hasConfirmed) {
        await this.deleteDocument(item[this.config.itemKey]);
        this.refresh({ isForced: true });
      }
    },
    async downloadDocument(item: any) {
      let uniqueDownload = false;
      if (this.config.actions?.download?.uniqueDownload) {
        // INFO: uniqueDownload is only used by tarif-covid
        uniqueDownload = this.config.actions?.download?.uniqueDownload(item);
      }
      const documentAsBlob: Blob = await DcmService.getObjectContentAsBlob(item[this.config.itemKey], uniqueDownload);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(documentAsBlob);
      link.download = this.getItemName(item);
      link.click();
      if (uniqueDownload) {
        this.refresh({ isForced: true });
      }
      URL.revokeObjectURL(link.href);
    },
    async openDocumentMetadata(item: any) {
      const editedMetadata = await this.$modal.show<false | DcmMetadataForm>(ViewOrEditDcmProperties, {
        item: Object.assign({}, item),
        metadataActions: this.config.actions?.metadata,
      });
      if (editedMetadata) {
        await DcmService.updateProperties(item[this.config.itemKey], editedMetadata);
        this.refresh({ isForced: true });
      }
    },
    async previewDocument(item: any, isEmbeded: boolean | undefined) {
      const blob: Blob = await DcmService.getObjectContentAsBlob(item[this.config.itemKey], false);
      const blobFile = new File([blob], this.getItemName(item), { type: blob.type });
      const fileURL = URL.createObjectURL(blobFile);

      // for PDF files: we can use `${fileURL}#toolbar=0` to hide toolbar, but it doesn't work with Firefox
      //   when user download pdf via embeded btn in toolbar:
      //   - in Firefox, file is downloaded as 'fileName' [defined in: new File([blob], 'fileName', { type: blob.type })]
      //   - but in Chrome, file is downloaded as 'hash' defined in blob

      if (isEmbeded) {
        this.$modal.show<boolean>(PreviewDocument, {
          fileName: this.getItemName(item),
          fileType: blob.type,
          fileURL: fileURL,
        });
      } else {
        open(fileURL);
      }
    },
    async refresh({
      isForced = false,
      isLaunchedByOptions = false,
    }: { isForced?: boolean; isLaunchedByOptions?: boolean } = {}) {
      this.loading = true;
      this.itemSelected = [];
      const query = await this.getQuery();

      // Compare query with previous
      const sameQThanPrev = _.isEqual(this.previousQuery, query);
      this.previousQuery = query;

      // Send request if not identical to previous or if isForcedd
      if (!sameQThanPrev || isForced) {
        const response = await DcmService.search(query);
        this.items = response.content;
        this.hasMoreElements = response.hasMoreElements;

        if (!this.hasLaunchFirstRequest) {
          this.hasLaunchFirstRequest = true;
        }

        if (this.getConfigForExpandedItem !== undefined && this.showExpand) {
          const configs = [];
          for (const item of this.items) {
            configs.push(this.getConfigForExpandedItem(item));
          }
          this.configOfExpandedItems = configs;
        }
      }

      // Update paging with totalElemnts
      if (!isLaunchedByOptions || !this.totalQuery.count || this.totalQuery.isInError) {
        await this.updateTotalElements(query, isLaunchedByOptions);
      }
      this.loading = false;
    },
    async updateTotalElements(query: DcmDocumentListQuery, isLaunchedByOptions: boolean) {
      if (!isLaunchedByOptions) {
        // reset total
        this.totalQuery.count = null;
        this.totalElements = 0;

        if (this.hasMoreElements && !this.totalQuery.isInError) {
          // deep copy query
          const totalQuery = this.getTotalQuery(query);

          await DcmService.search(totalQuery)
            .then(res => {
              res.hasMoreElements
                ? (this.totalElements = res.totalElements + 1)
                : (this.totalQuery.count = res.totalElements);
            })
            .catch(err => {
              stop();
              this.totalQuery.isInError = true;
              this.$snackbar.show("error on pagination (when updateTotalElements): fake pagination activated", 5000);
              console.error("ERROR on updateTotalElements: ", err);
            });
        }
      }

      let fakeCount =
        (this.options.page - 1 || 0) * (this.options.itemsPerPage ?? this.config.limit.default) + this.items.length;
      if (this.hasMoreElements) {
        fakeCount += 1;
      }
      this.totalElements = this.totalQuery.count ?? (fakeCount > this.totalElements ? fakeCount : this.totalElements);
    },
    async uploadDocument() {
      if (this.config.actions?.upload) {
        const docToCreate = await this.$modal.show<false | { file: File; metadata: DcmMetadataForm; path: string }>(
          UploadDocument,
          { uploadAction: this.config.actions.upload }
        );
        if (docToCreate) {
          const { file, metadata, path } = docToCreate;
          const doc = await DcmService.createDocument(file, metadata, path);

          // TODO: to be removed when backend automatically creates dcm:createdBy
          if (doc && doc["cmis:objectTypeId"] === "D:acsva:worker" && doc["cmis:createdBy"] && !doc["dcm:createdBy"]) {
            await DcmService.updateProperties(doc["cmis:objectId"], {
              "dcm:createdBy": doc["cmis:createdBy"].split("$")[0],
            });
          }
          // END TODO: to be removed when backend automatically creates dcm:createdBy

          this.refresh({ isForced: true });
        }
      }
    },
  },
});
